import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, TrendingUp, BarChart2, Award, BookOpen, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';

function fmt(n) {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'jt';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'rb';
  return (n || 0).toLocaleString('id-ID');
}

function MiniBar({ value, max, color = 'var(--in-blue)' }) {
  const pct = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 4;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{
        flex: 1, height: 6, borderRadius: 99,
        background: 'var(--in-border)',
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: color,
          borderRadius: 99,
          transition: 'width 0.6s ease',
        }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, minWidth: 36, textAlign: 'right', color: 'var(--in-text)' }}>
        {fmt(value)}
      </span>
    </div>
  );
}

const MEDAL = ['🥇', '🥈', '🥉'];

export default function ArticleStats({
  articles = [],
  totalViews = 0,
  avgViews = 0,
  label = 'Statistik Artikel',
  secondaryCol,
  secondaryLabel,
}) {
  const [view, setView] = useState('chart'); // chart | table

  if (!articles.length) return null;

  const maxViews = articles[0]?.viewCount || 1;
  const topArticle = articles[0];
  const totalArticles = articles.length;

  const barColors = [
    '#004A9F', '#0066CC', '#1A8FE3',
    '#38BDF8', '#7DD3FC', '#BAE6FD',
  ];

  return (
    <div style={{ marginTop: 40 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <TrendingUp size={18} style={{ color: 'var(--in-blue)', flexShrink: 0 }} />
        <div className="section-title" style={{ marginBottom: 0 }}>{label}</div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button
            className={`ki-tab${view === 'chart' ? ' active' : ''}`}
            onClick={() => setView('chart')}
            style={{ fontSize: 12, padding: '4px 12px' }}
          >
            <BarChart2 size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Grafik
          </button>
          <button
            className={`ki-tab${view === 'table' ? ' active' : ''}`}
            onClick={() => setView('table')}
            style={{ fontSize: 12, padding: '4px 12px' }}
          >
            Detail
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 12,
        marginBottom: 20,
      }}>
        {[
          {
            icon: <Eye size={16} />,
            val: fmt(totalViews),
            label: 'Total Pembacaan',
            sub: 'semua artikel',
            color: 'rgba(0,21,64,0.08)',
            iconColor: 'var(--in-blue)',
          },
          {
            icon: <BarChart2 size={16} />,
            val: fmt(avgViews),
            label: 'Rata-rata Pembaca',
            sub: 'per artikel',
            color: 'rgba(0,21,64,0.08)',
            iconColor: 'var(--in-blue)',
          },
          {
            icon: <BookOpen size={16} />,
            val: totalArticles,
            label: 'Artikel Publikasi',
            sub: 'terpublikasi',
            color: 'rgba(212,105,10,0.10)',
            iconColor: 'var(--in-orange)',
          },
          {
            icon: <Award size={16} />,
            val: fmt(topArticle?.viewCount),
            label: 'Tertinggi',
            sub: topArticle?.title?.slice(0, 22) + (topArticle?.title?.length > 22 ? '…' : ''),
            color: 'rgba(212,105,10,0.10)',
            iconColor: 'var(--in-orange)',
          },
        ].map(s => (
          <div key={s.label} style={{
            background: s.color,
            borderRadius: 12,
            padding: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}>
            <div style={{ color: s.iconColor }}>{s.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1, color: 'var(--in-text)' }}>{s.val}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--in-text)' }}>{s.label}</div>
            <div style={{ fontSize: 11, color: 'var(--in-text-muted)', marginTop: -2 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Chart view */}
      {view === 'chart' && (
        <div className="ki-card" style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: 12, color: 'var(--in-text-muted)', marginBottom: 16, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Peringkat berdasarkan jumlah pembaca
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {articles.map((a, i) => {
              const pctOfTotal = totalViews > 0 ? ((a.viewCount / totalViews) * 100).toFixed(1) : 0;
              const color = barColors[Math.min(i, barColors.length - 1)];
              return (
                <div key={a._id} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {/* Rank */}
                  <div style={{
                    width: 28, flexShrink: 0, textAlign: 'center',
                    fontSize: i < 3 ? 16 : 13,
                    fontWeight: i < 3 ? 'normal' : 700,
                    color: i < 3 ? 'inherit' : 'var(--in-text-muted)',
                  }}>
                    {i < 3 ? MEDAL[i] : `#${i + 1}`}
                  </div>

                  {/* Title + bar */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      marginBottom: 6,
                      color: 'var(--in-text)',
                    }}>
                      {a.slug
                        ? <a href={`/berita/${a.slug}`} target="_blank" rel="noopener noreferrer"
                            style={{ color: 'inherit', textDecoration: 'none' }}
                            onMouseOver={e => e.target.style.textDecoration = 'underline'}
                            onMouseOut={e => e.target.style.textDecoration = 'none'}
                          >{a.title}</a>
                        : a.title}
                    </div>
                    <MiniBar value={a.viewCount} max={maxViews} color={color} />
                  </div>

                  {/* Meta */}
                  <div style={{ flexShrink: 0, textAlign: 'right', minWidth: 60 }}>
                    <div style={{ fontSize: 11, color: 'var(--in-text-faint)' }}>{pctOfTotal}%</div>
                    {a.category && (
                      <div style={{
                        fontSize: 10, padding: '1px 7px', borderRadius: 99, marginTop: 4,
                        background: a.category.color ? `${a.category.color}22` : 'rgba(0,21,64,0.10)',
                        color: a.category.color || 'var(--in-blue)',
                        fontWeight: 600, display: 'inline-block',
                      }}>
                        {a.category.name}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Table view */}
      {view === 'table' && (
        <div className="ki-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="ki-table-wrap">
          <table className="ki-table">
            <thead>
              <tr>
                <th style={{ width: 32 }}>#</th>
                <th>Judul Artikel</th>
                {secondaryLabel && <th>{secondaryLabel}</th>}
                <th>Kategori</th>
                <th>Dipublikasi</th>
                <th style={{ textAlign: 'right' }}>
                  <Eye size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                  Dibaca
                </th>
                <th style={{ width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {articles.map((a, i) => {
                const secondaryVal = secondaryCol
                  ? (a[secondaryCol]?.name || '-')
                  : null;
                return (
                  <tr key={a._id}>
                    <td style={{ fontSize: 12, color: i < 3 ? 'var(--in-text)' : 'var(--in-text-faint)', fontWeight: 700 }}>
                      {i < 3 ? MEDAL[i] : i + 1}
                    </td>
                    <td style={{ fontWeight: 600, fontSize: 13, maxWidth: 260 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.title}
                      </div>
                    </td>
                    {secondaryLabel && (
                      <td style={{ fontSize: 13, color: 'var(--in-text-muted)' }}>{secondaryVal}</td>
                    )}
                    <td>
                      {a.category ? (
                        <span style={{
                          fontSize: 11, padding: '2px 9px', borderRadius: 99,
                          background: a.category.color ? `${a.category.color}22` : 'rgba(0,21,64,0.10)',
                          color: a.category.color || 'var(--in-blue)',
                          fontWeight: 600,
                        }}>
                          {a.category.name}
                        </span>
                      ) : '-'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--in-text-faint)', whiteSpace: 'nowrap' }}>
                      {a.publishedAt ? format(new Date(a.publishedAt), 'd MMM yyyy', { locale: id }) : '-'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 800, fontSize: 14, color: 'var(--in-text)' }}>
                      {fmt(a.viewCount)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {a.slug && (
                        <a href={`/berita/${a.slug}`} target="_blank" rel="noopener noreferrer"
                          style={{ color: 'var(--in-text-faint)', display: 'inline-flex' }}
                          title="Buka artikel"
                        >
                          <ExternalLink size={13} />
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
